/**
 * Image in/out marshalling for the Firefly editor embed (ADO-1412).
 *
 * Deliberately URL/Blob-only: NO `@adobe/data` BlobRef ever crosses the module
 * boundary (the editor bundle carries its own `@adobe/data` instance, so a
 * Bridgeway-made BlobRef would not resolve in the bundle's store). Image IN is the
 * Azure SAS URL the editor fetches itself; image OUT is a raw Blob the editor
 * hands back, which we turn into a `File` for the existing upload path.
 */

import {
	ALLOWED_IMAGE_MIME_TYPES,
	MAX_IMAGE_SIZE_LABEL,
} from '@/api/fileUploadApi';

/** 30MB — must match `fileUploadApi`'s cap (kept in sync via the shared label). */
export const MAX_EDITED_IMAGE_BYTES = 30 * 1024 * 1024;

const ALLOWED_MIME_SET = new Set<string>(ALLOWED_IMAGE_MIME_TYPES);

export type EditedImageInvalidReason = 'too_large' | 'invalid_type' | 'empty';

export class EditedImageInvalidError extends Error {
	readonly reason: EditedImageInvalidReason;

	constructor(message: string, reason: EditedImageInvalidReason) {
		super(message);
		this.name = 'EditedImageInvalidError';
		this.reason = reason;
	}
}

/**
 * Image IN. The editor opens an image by URL (it does its own fetch +
 * BlobRef internally), so marshalling is just validating we have a usable URL.
 * Returns the URL unchanged; throws if it is missing.
 */
export function resolveImageInputUrl(
	sasUrl: string | null | undefined,
): string {
	if (!sasUrl) {
		throw new EditedImageInvalidError(
			'No source image URL to open in the editor',
			'empty',
		);
	}
	return sasUrl;
}

/**
 * Image OUT. Convert the editor's edited-image Blob into a `File` ready for
 * `fileUploadService.upload`. Validates type + size so a malformed or oversize
 * result fails loudly here instead of silently at upload. The editor returns
 * PNG; we name accordingly.
 *
 * @param blob   The edited image bytes from the editor (or `null` if the user
 *               closed without editing — caller treats `null` as "no write-back").
 * @param baseName Optional base for the filename (defaults to `firefly-edit`).
 */
export function editedBlobToFile(
	blob: Blob | null,
	baseName = 'firefly-edit',
): File | null {
	if (blob === null) return null;

	if (blob.size === 0) {
		throw new EditedImageInvalidError('Edited image is empty', 'empty');
	}
	if (blob.size > MAX_EDITED_IMAGE_BYTES) {
		throw new EditedImageInvalidError(
			`Edited image exceeds ${MAX_IMAGE_SIZE_LABEL}`,
			'too_large',
		);
	}
	const type = blob.type || 'image/png';
	if (!ALLOWED_MIME_SET.has(type)) {
		throw new EditedImageInvalidError(
			`Edited image type ${type} is not an accepted image type`,
			'invalid_type',
		);
	}

	const ext = type === 'image/jpeg' ? 'jpg' : 'png';
	return new File([blob], `${baseName}.${ext}`, { type });
}
